import React from "react";
import { Helmet } from "react-helmet-async";
import larnacaFinikoudesImage from "../assets/cyprus/larnaca-finikoudes.webp";
import taxiImage from "../assets/taxi-1.jpg";
import { trackPhoneClick, trackWhatsAppClick } from "../utils/analytics";

export default function LarnacaAirportToLimassolPage() {
  const phoneNumber = "+35796613191";
  const whatsappLink =
    "https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi.";

  const reasons = [
    {
      title: "Direct airport pickup",
      text: "Meet your driver at Larnaca Airport and travel directly to your hotel, apartment, marina, office, or home in Limassol.",
    },
    {
      title: "24/7 availability",
      text: "Book early morning, late night, weekend, and holiday transfers with fast confirmation by phone or WhatsApp.",
    },
    {
      title: "Private transfer",
      text: "No shared shuttle, no waiting for other passengers, and enough space for your luggage after a long flight.",
    },
  ];

  const journeyDetails = [
    ["Route", "Larnaca Airport to Limassol"],
    ["Service", "Private airport taxi transfer"],
    ["Journey time", "Usually around 45 to 60 minutes"],
    ["Booking", "Phone or WhatsApp"],
    ["Availability", "24/7 by request"],
    ["Pickup", "Larnaca Airport arrivals area"],
  ];

  const flightBenefits = [
    "Better pickup timing when arrival times change",
    "Clear communication before your driver meets you",
    "Useful for late arrivals and early morning airport pickups",
    "Less stress if your flight lands later than expected",
  ];

  const faqs = [
    {
      question: "How long is the journey?",
      answer:
        "A taxi from Larnaca Airport to Limassol usually takes around 45 to 60 minutes, depending on traffic, exact destination, and time of day.",
    },
    {
      question: "How much does a taxi cost?",
      answer:
        "The guide price for a private taxi from Larnaca Airport to Limassol starts from EUR70. The final price can depend on pickup time, passengers, luggage, child seats, and exact destination.",
    },
    {
      question: "Do you monitor delayed flights?",
      answer:
        "Yes. Send your flight number when booking and your driver can follow the arrival information so pickup timing is adjusted if your flight is delayed.",
    },
    {
      question: "Can I pre-book?",
      answer:
        "Yes. Pre-booking is recommended for airport arrivals, late-night transfers, family travel, and busy travel dates. You can reserve by phone or WhatsApp.",
    },
    {
      question: "Do you provide child seats?",
      answer:
        "Child seats can be requested in advance. Please mention the child's age and seat requirement when booking so availability can be confirmed.",
    },
  ];

  return (
    <main className="min-h-screen bg-neutral-950 text-white">
      <Helmet>
        <title>
          Larnaca Airport to Limassol Taxi | Fixed Price Airport Transfer
        </title>
        <meta
          name="description"
          content="Book a reliable taxi from Larnaca Airport to Limassol. Fixed prices, 24/7 availability, flight monitoring and WhatsApp booking."
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Larnaca Airport to Limassol Taxi | Fixed Price Airport Transfer"
        />
        <meta
          property="og:description"
          content="Book a reliable taxi from Larnaca Airport to Limassol. Fixed prices, 24/7 availability, flight monitoring and WhatsApp booking."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://limassolairporttaxi.com/larnaca-airport-to-limassol"
        />
        <meta
          property="og:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Larnaca Airport to Limassol Taxi | Fixed Price Airport Transfer"
        />
        <meta
          name="twitter:description"
          content="Book a reliable taxi from Larnaca Airport to Limassol. Fixed prices, 24/7 availability, flight monitoring and WhatsApp booking."
        />
        <meta
          name="twitter:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <link
          rel="canonical"
          href="https://limassolairporttaxi.com/larnaca-airport-to-limassol"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <section
        className="relative overflow-hidden bg-neutral-900 px-6 pb-28 pt-40 md:pb-36 md:pt-48"
      >
        <img
          src={larnacaFinikoudesImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/35 to-black/50" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="font-semibold text-yellow-300">
              Larnaca Airport Transfer
            </p>

            <h1 className="mt-4 text-5xl font-bold text-yellow-400 md:text-7xl">
              Larnaca Airport to Limassol Taxi
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-8 text-neutral-200">
              Book a reliable private taxi from Larnaca Airport to Limassol
              with fixed price information, 24/7 availability, flight
              monitoring, and quick WhatsApp booking.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                href={`tel:${phoneNumber}`}
                onClick={() => trackPhoneClick("larnaca_airport_to_limassol_hero")}
                className="rounded-2xl bg-yellow-400 px-8 py-4 text-center font-bold text-neutral-950 transition-colors duration-200 hover:bg-yellow-300"
              >
                Call Now
              </a>

              <a
                href={whatsappLink}
                onClick={() => trackWhatsAppClick("larnaca_airport_to_limassol_hero")}
                target="_blank"
                rel="noreferrer"
                className="rounded-2xl border border-white/10 bg-white/[0.04] px-8 py-4 text-center font-bold text-white transition-colors duration-200 hover:border-yellow-400/50 hover:text-yellow-300"
              >
                WhatsApp Booking
              </a>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-neutral-400">
              Private airport transfer
            </p>

            <div className="mt-5 rounded-3xl border border-yellow-400/20 bg-black/40 p-7">
              <p className="text-neutral-400">Guide price</p>
              <p className="mt-3 text-5xl font-bold text-yellow-300">
                From EUR70
              </p>
              <p className="mt-4 leading-7 text-neutral-400">
                Door-to-door taxi transfer from Larnaca Airport arrivals to
                Limassol hotels, apartments, marina, old town, and nearby areas.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-3xl">
          <p className="font-semibold text-yellow-300">Why Choose a Taxi</p>
          <h2 className="mt-3 text-4xl font-bold text-yellow-400">
            Why choose a taxi from Larnaca Airport to Limassol
          </h2>
          <p className="mt-4 leading-7 text-neutral-400">
            A private airport taxi is the simplest way to reach Limassol after
            your flight, especially with luggage, children, late arrivals, or
            direct hotel check-in plans.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {reasons.map((reason) => (
            <article
              key={reason.title}
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-7"
            >
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-yellow-400/10 text-yellow-400">
                ✓
              </div>
              <h3 className="text-2xl font-bold text-yellow-400">
                {reason.title}
              </h3>
              <p className="mt-4 leading-7 text-neutral-400">{reason.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-neutral-900 px-6 py-20">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="font-semibold text-yellow-300">Journey Details</p>
            <h2 className="mt-3 text-4xl font-bold text-yellow-400">
              Larnaca Airport to Limassol journey details
            </h2>
            <p className="mt-5 leading-8 text-neutral-300">
              Your transfer starts at Larnaca Airport and finishes directly at
              your Limassol address. The route is popular for tourists,
              residents, business travellers, and families arriving in Cyprus.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {journeyDetails.map(([label, value]) => (
              <div
                key={label}
                className="rounded-3xl border border-white/10 bg-white/[0.04] p-6"
              >
                <p className="text-sm text-neutral-400">{label}</p>
                <p className="mt-2 font-bold text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04]">
          <div className="grid md:grid-cols-2">
            <div className="relative min-h-[360px]">
              <img
                src={taxiImage}
                alt="Taxi transfer from Larnaca Airport to Limassol"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
            </div>

            <div className="p-8 md:p-12">
              <p className="font-semibold text-yellow-300">Fixed Pricing</p>
              <h2 className="mt-3 text-4xl font-bold text-yellow-400">
                Fixed pricing information
              </h2>
              <p className="mt-5 text-lg leading-8 text-neutral-300">
                The guide price for a standard private taxi from Larnaca Airport
                to Limassol starts from EUR70. We confirm your transfer price
                before pickup so you know the cost before you travel.
              </p>
              <p className="mt-4 leading-7 text-neutral-400">
                Final pricing can depend on exact destination, pickup time,
                passengers, luggage, holidays, waiting time, and special
                requests such as child seats.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-neutral-900 px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="font-semibold text-yellow-300">Flight Monitoring</p>
            <h2 className="mt-3 text-4xl font-bold text-yellow-400">
              Flight tracking benefits
            </h2>
            <p className="mt-4 leading-7 text-neutral-400">
              Send your flight number when you book. Flight monitoring helps us
              coordinate airport pickup if your arrival time changes.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {flightBenefits.map((benefit) => (
              <div
                key={benefit}
                className="rounded-3xl border border-white/10 bg-white/[0.04] p-6"
              >
                <div className="mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-yellow-400/10 text-yellow-400">
                  ✓
                </div>
                <p className="font-semibold leading-7 text-neutral-200">
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <div className="mb-12 text-center">
          <p className="font-semibold text-yellow-300">Airport Transfer FAQ</p>
          <h2 className="mt-3 text-4xl font-bold text-yellow-400">FAQ</h2>
        </div>

        <div className="space-y-5">
          {faqs.map((faq) => (
            <div
              key={faq.question}
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-6"
            >
              <h3 className="text-xl font-bold text-yellow-400">
                {faq.question}
              </h3>
              <p className="mt-3 leading-7 text-neutral-400">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-20">
        <div className="mx-auto max-w-7xl rounded-3xl border border-yellow-400/20 bg-white/[0.04] p-8 text-center md:p-12">
          <p className="font-semibold text-yellow-300">Book Your Transfer</p>
          <h2 className="mt-3 text-4xl font-bold text-yellow-400 md:text-5xl">
            Need a taxi from Larnaca Airport to Limassol?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-neutral-300">
            Call now or send a WhatsApp message with your flight number,
            arrival date, passengers, luggage, and Limassol destination.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href={`tel:${phoneNumber}`}
              onClick={() => trackPhoneClick("larnaca_airport_to_limassol_final_cta")}
              className="rounded-2xl bg-yellow-400 px-8 py-4 text-center font-bold text-neutral-950 transition-colors duration-200 hover:bg-yellow-300"
            >
              Call Now
            </a>

            <a
              href={whatsappLink}
              onClick={() => trackWhatsAppClick("larnaca_airport_to_limassol_final_cta")}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border border-white/10 bg-neutral-950 px-8 py-4 text-center font-bold text-white transition-colors duration-200 hover:border-yellow-400/50 hover:text-yellow-300"
            >
              WhatsApp Booking
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
